import { useState, useEffect, useCallback } from 'react'
import { COLORS, TYPE } from './tokens'
import { supabase } from '../../shared/lib/supabase'
import CoverageCardStrip from './CoverageCardStrip'
import CurrentReviewView from './CurrentReviewView'
import ReviewHistoryView from './ReviewHistoryView'
import EmptyState from './EmptyState'
import useInsuranceReview from './useInsuranceReview'
import UploadState from './UploadState'
import PipelineTracker from './PipelineTracker'
import PipelineFailureState from './PipelineFailureState'
import useInsuranceReviewPipeline from './useInsuranceReviewPipeline'

const SUB_TABS = [
  { id: 'current', label: 'Current Review' },
  { id: 'history', label: 'Review History' }
]

function SubTabBar({ active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: '24px',
      padding: '0 64px',
      borderBottom: `1px solid ${COLORS.hairline}`,
      background: COLORS.panelBg
    }}>
      {SUB_TABS.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            ...TYPE.body,
            background: 'transparent',
            border: 'none',
            padding: '14px 4px',
            color: active === t.id ? COLORS.bodyText : COLORS.mutedText,
            borderBottom: active === t.id ? `2px solid ${COLORS.accent}` : '2px solid transparent',
            cursor: 'pointer'
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

export default function CyberInsuranceReviewTab({ client }) {
  const [subTab, setSubTab] = useState('current')
  const [hasCoverage, setHasCoverage] = useState(!!client?.has_cyber_insurance)
  const [p95, setP95] = useState(0.95)
  const [runNewContext, setRunNewContext] = useState(null)
  // The client prop can arrive undefined on first render (AssessmentContext
  // hydrates async). Sync the toggle when the underlying field materializes
  // or changes, otherwise the initial mount reads false and stays false.
  useEffect(() => {
    setHasCoverage(!!client?.has_cyber_insurance)
  }, [client?.has_cyber_insurance])
  const { loading, error, term, review, findings, sourceDocuments, refetch: refetchReview } = useInsuranceReview(client?.id)
  const pipeline = useInsuranceReviewPipeline({
    clientId: client?.id,
    onReviewReady: () => {
      refetchReview()
      setRunNewContext(null)
    }
  })

  // Persist toggle changes back to clients.has_cyber_insurance so the state
  // survives navigation away from and back to this tab. Optimistic update:
  // reflect the click immediately; revert on failure.
  const handleToggle = useCallback(async (next) => {
    setHasCoverage(next)
    if (!client?.id) return
    const { error: updErr } = await supabase
      .from('clients')
      .update({ has_cyber_insurance: next })
      .eq('id', client.id)
    if (updErr) {
      console.error('[insurance-review] failed to persist has_cyber_insurance:', updErr)
      setHasCoverage(!next)
    }
  }, [client?.id])

  return (
    <div style={{ background: COLORS.bodyBg, minHeight: '100vh' }}>
      <CoverageCardStrip
        hasCoverage={hasCoverage}
        onToggle={handleToggle}
        p95={p95}
        onP95Change={setP95}
      />
      <SubTabBar active={subTab} onChange={setSubTab} />
      {!hasCoverage ? (
        <EmptyState reason="no-coverage" />
      ) : loading ? (
        <div style={{ padding: '80px 64px', color: COLORS.mutedText }}>Loading review…</div>
      ) : error ? (
        <div style={{ padding: '80px 64px', color: COLORS.mutedText }}>Error loading review: {error}</div>
      ) : review && !runNewContext ? (
        subTab === 'current'
          ? <CurrentReviewView
              term={term}
              review={review}
              findings={findings}
              sourceDocuments={sourceDocuments}
              onReviewRefetch={refetchReview}
              onRunNewReview={(ctx) => setRunNewContext(ctx)}
            />
          : <ReviewHistoryView />
      ) : subTab === 'history' ? (
        <ReviewHistoryView />
      ) : pipeline.view === 'running' ? (
        <PipelineTracker stages={pipeline.stages} runStartedAt={pipeline.runStartedAt} activeStageStartedAt={pipeline.activeStageStartedAt} />
      ) : pipeline.view === 'failed' ? (
        <PipelineFailureState stages={pipeline.stages} error={pipeline.error} onRetry={pipeline.retry} onStartOver={pipeline.startOver} />
      ) : pipeline.view === 'done' ? (
        <div style={{ padding: '80px 64px', color: COLORS.mutedText }}>Finalizing review…</div>
      ) : (
        <UploadState
          clientId={client.id}
          onSubmit={({ mode, documents }) =>
            pipeline.submit({
              mode,
              documents,
              reviewKind: runNewContext?.review_kind,
              parentReviewId: runNewContext?.parent_review_id,
              policyTermId: runNewContext?.policy_term_id
            })
          }
          disabled={false}
        />
      )}
    </div>
  )
}
